import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';
import {
  dismissStripeOnboarding,
  fetchOrganizationStripeAccount,
  fetchTeamStripeAccount,
  findLinkableStripeSource,
  isStripeOnboardingDismissed,
  isStripeReady,
  linkSharedStripeToEntities,
  linkStripeConnectAccount,
  syncStripeConnect,
  type ManagedStripeEntity,
  type StripeConnectScope,
} from '../../lib/stripeConnect';
import {
  getStripePayoutPreference,
  setStripePayoutPreference,
  type StripePayoutPreference,
} from '../../lib/stripePayoutPreference';
import { StripeConnectOnboardingModal } from './StripeConnectOnboardingModal';
import { StripePayoutAccountChoiceModal } from './StripePayoutAccountChoiceModal';

type PendingOnboarding = {
  scope: StripeConnectScope;
  entityId: string;
  entityName: string;
};

function canManageTeamStripe(role: string | undefined): boolean {
  return role === 'head_coach';
}

function canManageOrgStripe(role: string | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

export function StripeConnectOnboardingGate() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const {
    activeContext,
    loading: roleLoading,
    availableTeams,
    availableOrganizations,
  } = useRole();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [checking, setChecking] = useState(false);
  const [pending, setPending] = useState<PendingOnboarding | null>(null);
  const [showPayoutChoice, setShowPayoutChoice] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [sharedHint, setSharedHint] = useState<string | null>(null);
  const [linkableSource, setLinkableSource] = useState<
    (ManagedStripeEntity & { account: { stripe_account_id: string | null } }) | null
  >(null);

  const manageableEntities = useMemo((): ManagedStripeEntity[] => {
    const teams = availableTeams
      .filter((t) => t.role === 'head_coach')
      .map((t) => ({ scope: 'team' as const, id: t.id, name: t.name }));
    const orgs = availableOrganizations
      .filter((o) => o.role === 'owner' || o.role === 'admin')
      .map((o) => ({ scope: 'organization' as const, id: o.id, name: o.name }));
    return [...teams, ...orgs];
  }, [availableTeams, availableOrganizations]);

  const dualRoleContext = useMemo(() => {
    const teams = manageableEntities.filter((e) => e.scope === 'team');
    const orgs = manageableEntities.filter((e) => e.scope === 'organization');
    if (!teams.length || !orgs.length) return null;
    return {
      teamName: teams[0].name,
      organizationName: orgs[0].name,
      teams,
      orgs,
    };
  }, [manageableEntities]);

  const isBlockedRoute = useMemo(() => {
    const path = location.pathname;
    return (
      path === '/login' ||
      path === '/register' ||
      path === '/invite' ||
      path === '/forgot-password' ||
      path.startsWith('/site-admin')
    );
  }, [location.pathname]);

  const tryLinkFromSharedSource = useCallback(
    async (target: PendingOnboarding): Promise<boolean> => {
      if (getStripePayoutPreference() !== 'shared') return false;

      const source = await findLinkableStripeSource(
        manageableEntities.filter(
          (e) => !(e.scope === target.scope && e.id === target.entityId),
        ),
      );
      if (!source) return false;

      const targetAccount =
        target.scope === 'team'
          ? await fetchTeamStripeAccount(target.entityId)
          : await fetchOrganizationStripeAccount(target.entityId);

      if (
        targetAccount?.stripe_account_id &&
        targetAccount.stripe_account_id === source.account.stripe_account_id
      ) {
        return true;
      }

      await linkStripeConnectAccount(
        source.scope,
        source.id,
        target.scope,
        target.entityId,
      );
      setSyncMessage(
        `Using the same Stripe payout account as ${source.name} for ${target.entityName}.`,
      );
      return true;
    },
    [manageableEntities],
  );

  const resolvePending = useCallback(async () => {
    if (!isAuthenticated || roleLoading || isBlockedRoute) {
      setPending(null);
      setShowPayoutChoice(false);
      return;
    }

    let scope: StripeConnectScope | null = null;
    let entityId = '';
    let entityName = '';
    let canManage = false;

    if (activeContext.type === 'coach') {
      scope = 'team';
      entityId = activeContext.teamId;
      entityName = activeContext.teamName;
      canManage = canManageTeamStripe(activeContext.role);
    } else if (activeContext.type === 'organizer') {
      scope = 'organization';
      entityId = activeContext.organizationId;
      entityName = activeContext.organizationName;
      canManage = canManageOrgStripe(activeContext.role);
    }

    if (!scope || !entityId || !canManage) {
      setPending(null);
      setShowPayoutChoice(false);
      return;
    }

    if (isStripeOnboardingDismissed(scope, entityId)) {
      setPending(null);
      setShowPayoutChoice(false);
      return;
    }

    setChecking(true);
    try {
      const account =
        scope === 'team'
          ? await fetchTeamStripeAccount(entityId)
          : await fetchOrganizationStripeAccount(entityId);

      if (isStripeReady(account)) {
        setPending(null);
        setShowPayoutChoice(false);
        return;
      }

      const pendingTarget: PendingOnboarding = { scope, entityId, entityName };

      if (dualRoleContext && !getStripePayoutPreference()) {
        setPending(pendingTarget);
        setShowPayoutChoice(true);
        setSharedHint(null);
        return;
      }

      if (getStripePayoutPreference() === 'shared') {
        const linked = await tryLinkFromSharedSource(pendingTarget);
        if (linked) {
          setPending(null);
          setShowPayoutChoice(false);
          setSharedHint(
            dualRoleContext
              ? `One Stripe account will receive team and event payouts for easier bookkeeping.`
              : null,
          );
          return;
        }
        setSharedHint(
          dualRoleContext
            ? `After you connect Stripe, the same payout account will be used for ${dualRoleContext.teamName} and ${dualRoleContext.organizationName}.`
            : null,
        );
      } else {
        setSharedHint(null);
      }

      setPending(pendingTarget);
      setShowPayoutChoice(false);

      const source = await findLinkableStripeSource(
        manageableEntities.filter(
          (e) => !(e.scope === scope && e.id === entityId),
        ),
      );
      setLinkableSource(source);
    } finally {
      setChecking(false);
    }
  }, [
    activeContext,
    dualRoleContext,
    isAuthenticated,
    isBlockedRoute,
    roleLoading,
    tryLinkFromSharedSource,
  ]);

  useEffect(() => {
    resolvePending();
  }, [resolvePending]);

  useEffect(() => {
    const connectStatus = searchParams.get('stripe_connect');
    const scopeParam = searchParams.get('scope') as StripeConnectScope | null;
    const entityId = searchParams.get('entityId');

    if (!connectStatus || !scopeParam || !entityId) return;
    if (scopeParam !== 'team' && scopeParam !== 'organization') return;

    const run = async () => {
      try {
        await syncStripeConnect(scopeParam, entityId);

        if (getStripePayoutPreference() === 'shared') {
          const sourceEntity = manageableEntities.find(
            (e) => e.scope === scopeParam && e.id === entityId,
          );
          if (sourceEntity) {
            const others = manageableEntities.filter(
              (e) => !(e.scope === scopeParam && e.id === entityId),
            );
            const linked = await linkSharedStripeToEntities(sourceEntity, others);
            if (linked > 0) {
              setSyncMessage(
                'Stripe connected. The same payout account is now linked to your team and event organization.',
              );
            } else {
              setSyncMessage(
                connectStatus === 'complete'
                  ? 'Stripe account updated. You can accept payments once Stripe finishes verification.'
                  : 'Continue Stripe setup when you are ready.',
              );
            }
          }
        } else {
          setSyncMessage(
            connectStatus === 'complete'
              ? 'Stripe account updated. You can accept payments once Stripe finishes verification.'
              : 'Continue Stripe setup when you are ready.',
          );
        }
      } catch {
        // non-fatal
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('stripe_connect');
        next.delete('scope');
        next.delete('entityId');
        setSearchParams(next, { replace: true });
        resolvePending();
      }
    };

    run();
  }, [searchParams, setSearchParams, resolvePending, manageableEntities]);

  const handlePayoutChoice = async (preference: StripePayoutPreference) => {
    setStripePayoutPreference(preference);
    setShowPayoutChoice(false);

    if (!pending) return;

    if (preference === 'shared') {
      const linked = await tryLinkFromSharedSource(pending);
      if (linked) {
        setPending(null);
        return;
      }
      setSharedHint(
        dualRoleContext
          ? `Connect Stripe once — payouts for ${dualRoleContext.teamName} and ${dualRoleContext.organizationName} will use the same bank account.`
          : null,
      );
    } else {
      setSharedHint(null);
    }
  };

  const handleClose = (mode: 'later' | 'permanent') => {
    if (pending) {
      dismissStripeOnboarding(pending.scope, pending.entityId, mode);
    }
    setPending(null);
    setShowPayoutChoice(false);
  };

  const toast = syncMessage ? (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-[70]">
      <div className="bg-green-50 border border-green-200 text-green-900 rounded-xl px-4 py-3 text-sm shadow-lg flex items-start justify-between gap-3">
        <span>{syncMessage}</span>
        <button
          type="button"
          onClick={() => setSyncMessage(null)}
          className="text-green-700 hover:text-green-900 font-medium shrink-0"
        >
          Dismiss
        </button>
      </div>
    </div>
  ) : null;

  if (authLoading || roleLoading || checking) {
    return toast;
  }

  if (showPayoutChoice && pending && dualRoleContext) {
    return (
      <>
        <StripePayoutAccountChoiceModal
          teamName={dualRoleContext.teamName}
          organizationName={dualRoleContext.organizationName}
          onChoose={handlePayoutChoice}
          onClose={() => handleClose('later')}
        />
        {toast}
      </>
    );
  }

  if (!pending) {
    return toast;
  }

  return (
    <>
      <StripeConnectOnboardingModal
        scope={pending.scope}
        entityId={pending.entityId}
        entityName={pending.entityName}
        sharedPayoutHint={sharedHint}
        linkableSource={linkableSource}
        onClose={handleClose}
        onConnected={() => resolvePending()}
      />
      {toast}
    </>
  );
}
